import { fixTypeCompactCodeFromLabel } from '@/utils/fix-type-compact-code';
import {
  escapeMapPopupHtml,
  mapPopupRowHtml,
  mapTelemetryPopupRows,
} from '@/utils/map-telemetry-popup-rows';
import { getGoogleMapsLink } from '@/utils/map/google';
import { trackPopupPinIconSvg } from '@/utils/track-vertex-popup-icons';

export type CurrentPositionPopupFacts = {
  /** Device label; omitted / empty → no title row. */
  label?: string | null;
  address?: string | null;
  /** GeoJSON [lng, lat]; when valid, the address links to Google Maps. */
  location?: readonly [number, number] | null;
  timestampSec?: number | null;
  battery?: number | null;
  /** v2 current-positions fix string, e.g. "gps". */
  fix?: string | null;
  accuracyMeters?: number | null;
  exceptionTitles?: readonly string[];
  /** Expanded convoy hint under telemetry. */
  expandedHint?: boolean;
};

function googleMapsHref(
  location: CurrentPositionPopupFacts['location'],
): string | null {
  if (!Array.isArray(location) || location.length < 2) return null;
  const [lng, lat] = location;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return null;
  }
  return getGoogleMapsLink({ lat, lng });
}

function exceptionPillsHtml(titles: readonly string[]): string {
  if (titles.length === 0) return '';
  const pills = titles
    .map(
      (title) =>
        `<span class="rounded bg-red-600 px-1 py-px text-[9px] font-semibold tracking-wide text-white">${escapeMapPopupHtml(title)}</span>`,
    )
    .join('');
  return `<div class="mt-0.5 mb-1 flex flex-wrap gap-0.5">${pills}</div>`;
}

/** Popup HTML for a live / current device position pin. */
export function formatCurrentPositionPopup(
  facts: CurrentPositionPopupFacts,
): string {
  const parts: string[] = [];

  const label = facts.label?.trim();
  if (label) {
    parts.push(
      `<div class="gpx-track-vertex-popup__title">${escapeMapPopupHtml(label)}</div>`,
    );
  }

  if (facts.exceptionTitles && facts.exceptionTitles.length > 0) {
    parts.push(exceptionPillsHtml(facts.exceptionTitles));
  }

  const rows: string[] = [];

  const hasAddress =
    facts.address != null && String(facts.address).trim() !== '';
  const addressHtml = escapeMapPopupHtml(
    hasAddress ? String(facts.address) : '—',
  );
  const mapsHref = hasAddress ? googleMapsHref(facts.location) : null;
  const addressValueHtml = mapsHref
    ? `<a class="gpx-track-vertex-popup__address-link" href="${escapeMapPopupHtml(mapsHref)}" target="_blank" rel="noopener noreferrer">${addressHtml}</a>`
    : addressHtml;
  rows.push(
    mapPopupRowHtml(
      trackPopupPinIconSvg(),
      `<span class="gpx-track-vertex-popup__address">${addressValueHtml}</span>`,
    ),
  );

  const fixTypeCode =
    facts.fix !== null && facts.fix !== undefined
      ? fixTypeCompactCodeFromLabel(facts.fix)
      : undefined;

  rows.push(
    ...mapTelemetryPopupRows(
      {
        timestampSec: facts.timestampSec,
        battery: facts.battery,
        fixTypeCode,
        accuracyMeters: facts.accuracyMeters,
      },
      { emphasizeTime: false },
    ),
  );

  parts.push(...rows);

  if (facts.expandedHint) {
    parts.push(
      `<div class="gpx-track-vertex-popup__value gpx-track-vertex-popup__value--muted mt-1">Click to group trackers</div>`,
    );
  }

  if (parts.length === 0) {
    return '—';
  }

  return `<div class="gpx-track-vertex-popup">${parts.join('')}</div>`;
}
